import { type KeyboardEvent, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { clsx } from 'clsx'

import { useTravelSearchContext } from '../../context/TravelSearchProvider'

interface Props {
  onewayDisabled: boolean
}

type JourneyMode = 'return' | 'oneWay' | 'dayTrip'

const OnewayReturn: React.FC<Props> = ({ onewayDisabled }) => {
  const { t } = useTranslation()
  const buttonRefs = useRef<Record<JourneyMode, HTMLButtonElement | null>>({
    dayTrip: null,
    oneWay: null,
    return: null,
  })
  const {
    dispatch,
    state: { isDayTrip, isOneWay, showDayTripOffer },
  } = useTravelSearchContext()

  function handleSelected(isOneWay: boolean) {
    dispatch({ isOneWay, type: 'SET_ONEWAY' })
    dispatch({ isDayTrip: false, type: 'SET_DAY_TRIP' })
  }

  function handleDayTripSelected() {
    dispatch({ isOneWay: true, type: 'SET_ONEWAY' })
    dispatch({ isDayTrip: true, type: 'SET_DAY_TRIP' })
  }

  const selectedMode: JourneyMode = isDayTrip ? 'dayTrip' : isOneWay ? 'oneWay' : 'return'
  const availableModes: JourneyMode[] = ['return']
  if (!onewayDisabled) availableModes.push('oneWay')
  if (showDayTripOffer && !onewayDisabled) availableModes.push('dayTrip')
  const tabStopMode = availableModes.includes(selectedMode) ? selectedMode : 'return'

  function selectMode(mode: JourneyMode) {
    if (mode === 'dayTrip') {
      handleDayTripSelected()
    } else {
      handleSelected(mode === 'oneWay')
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLButtonElement>, currentMode: JourneyMode) {
    const currentIndex = availableModes.indexOf(currentMode)
    let nextIndex: number | undefined

    if (event.key === 'ArrowRight') {
      nextIndex = (currentIndex + 1) % availableModes.length
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (currentIndex - 1 + availableModes.length) % availableModes.length
    }

    if (nextIndex === undefined) return

    event.preventDefault()
    const nextMode = availableModes[nextIndex]
    selectMode(nextMode)
    buttonRefs.current[nextMode]?.focus()
  }

  return (
    <div
      aria-label={`${t('RETURN')} / ${t('ONE-WAY')}`}
      className="flex h-11.25 w-full cursor-pointer flex-row items-center border border-solid border-semantic-global-border-light bg-white px-semantic-global-2xs md:w-fit"
      id="returnOneway"
      role="radiogroup"
    >
      <button
        aria-checked={selectedMode === 'return'}
        className={clsx(
          'travel-search-oneway-return-tab relative h-8.75 min-w-0 flex-1 truncate cursor-pointer px-semantic-global-md text-semantic-global-text-default focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-semantic-global-primary-default md:flex-none',
          selectedMode === 'return' ? 'typography-body-md-medium' : 'typography-body-md-regular',
          !isOneWay && !isDayTrip ? 'bg-semantic-global-primary-lightest' : 'bg-white'
        )}
        data-e2e="returnTab"
        data-e2e-selected={!isOneWay ? !isDayTrip : null}
        id="returnTab"
        onKeyDown={(event) => handleKeyDown(event, 'return')}
        onClick={() => handleSelected(false)}
        ref={(element) => {
          buttonRefs.current.return = element
        }}
        role="radio"
        tabIndex={tabStopMode === 'return' ? 0 : -1}
        type="button"
      >
        {t('RETURN')}
      </button>
      <button
        aria-checked={selectedMode === 'oneWay'}
        className={clsx(
          'travel-search-oneway-return-tab relative h-8.75 min-w-0 flex-1 truncate px-semantic-global-md focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-semantic-global-primary-default md:flex-none',
          selectedMode === 'oneWay' ? 'typography-body-md-medium' : 'typography-body-md-regular',
          isOneWay && !onewayDisabled && !isDayTrip ? 'bg-semantic-global-primary-lightest' : 'bg-white',
          onewayDisabled ? 'cursor-not-allowed text-semantic-global-disabled-default' : 'cursor-pointer text-semantic-global-text-default'
        )}
        data-e2e="oneWayTab"
        data-e2e-selected={isOneWay ? !isDayTrip : null}
        disabled={onewayDisabled}
        id="onewayTab"
        onKeyDown={(event) => handleKeyDown(event, 'oneWay')}
        onClick={() => handleSelected(true)}
        ref={(element) => {
          buttonRefs.current.oneWay = element
        }}
        role="radio"
        tabIndex={tabStopMode === 'oneWay' ? 0 : -1}
        type="button"
      >
        {t('ONE-WAY')}
      </button>
      <button
        aria-checked={selectedMode === 'dayTrip'}
        className={clsx(
          'travel-search-oneway-return-tab relative h-8.75 min-w-0 flex-1 truncate cursor-pointer px-semantic-global-md text-semantic-global-text-default focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-semantic-global-primary-default md:flex-none',
          selectedMode === 'dayTrip' ? 'typography-body-md-medium' : 'typography-body-md-regular',
          isDayTrip ? 'bg-semantic-global-primary-lightest' : 'bg-white',
          !showDayTripOffer && 'hidden'
        )}
        data-e2e="dayTripTab"
        data-e2e-selected={isDayTrip}
        disabled={onewayDisabled}
        id="daytripTab"
        onKeyDown={(event) => handleKeyDown(event, 'dayTrip')}
        onClick={() => handleDayTripSelected()}
        ref={(element) => {
          buttonRefs.current.dayTrip = element
        }}
        role="radio"
        tabIndex={tabStopMode === 'dayTrip' ? 0 : -1}
        type="button"
      >
        {t('DAY-TRIP')}
      </button>
    </div>
  )
}

export default OnewayReturn
